import React, { FC, useMemo, useState } from 'react'

import { FieldErrors, UseFormTrigger, UseFormWatch } from 'react-hook-form'
import { UseFormSetValue } from 'react-hook-form/dist/types/form'

import { Search, TableSortType } from 'src/components/molecules'
import { ExternalAssignUserEditTableBody } from 'src/components/molecules/projects/ExternalAssignUserEditTableBody'
import { ExternalAssignUserEditTableTop } from 'src/components/molecules/projects/ExternalAssignUserEditTableTop'
import { ExternalAssignUserSortItemType } from 'src/components/molecules/projects/ExternalAssignUserTableHead'
import { InternalAssignUserEditTableBody } from 'src/components/molecules/projects/InternalAssignUserEditTableBody'
import { InternalAssignUserEditTableTop } from 'src/components/molecules/projects/InternalAssignUserEditTableTop'
import { InternalAssignUserSortItemType } from 'src/components/molecules/projects/InternalAssignUserTableHead'
import { NoAssignedUserNotice } from 'src/components/molecules/projects/NoAssignedUserNotice'
import { ProjectAssignSegmentedControls } from 'src/components/molecules/projects/ProjectAssignSegmentedControls'

import { useGetExternalUsers, useGetUsers } from 'src/hooks'
import { ProfileEntity, UpsertProjectFormEntity, ProjectDetailEntity } from 'src/types'
import { CompassRoleEntity } from 'src/types/entities/CompassRole'

type Props = {
  profile: ProfileEntity
  compassRoles: CompassRoleEntity[]
  project: ProjectDetailEntity
  setValue: UseFormSetValue<UpsertProjectFormEntity>
  watch: UseFormWatch<UpsertProjectFormEntity>
  formErrors: FieldErrors<UpsertProjectFormEntity>
  trigger: UseFormTrigger<UpsertProjectFormEntity>
  defaultSelectedTab?: 'internal' | 'external'
  className?: string
}

export const EditAssignTableWithSegmentedControls: FC<Props> = ({
  profile,
  compassRoles,
  project,
  watch,
  setValue,
  formErrors,
  trigger,
  defaultSelectedTab = 'internal',
  className,
}) => {
  const [selectedTab, setSelectedTab] = useState<'internal' | 'external'>(defaultSelectedTab)

  const [internalSortItem, setInternalSortItem] =
    useState<InternalAssignUserSortItemType>('assignedAt')
  const [internalSortType, setInternalSortType] = useState<TableSortType>('desc')

  const [externalSortItem, setExternalSortItem] =
    useState<ExternalAssignUserSortItemType>('assignedAt')
  const [externalSortType, setExternalSortType] = useState<TableSortType>('desc')

  const [searchWord, setSearchWord] = useState<string | undefined>()

  const { users: internalUsers } = useGetUsers()
  const { externalUsers } = useGetExternalUsers()

  const filteredInternalUsers = useMemo(() => {
    const internalAssignedUsers = [...watch('projectUsers')]

    if (!searchWord) {
      return internalAssignedUsers
    }

    return internalAssignedUsers.filter(user => {
      const userKeywords = internalUsers.find(internalUser => internalUser.id === user.id)?.keywords
      const hasKeywords = userKeywords?.some(keyword => keyword.includes(searchWord)) ?? false
      const userCustomRoles = internalUsers.find(
        internalUser => internalUser.id === user.id
      )?.customRoles
      const hasCustomRoles =
        userCustomRoles?.some(customRole => customRole.name.includes(searchWord)) ?? false

      return (
        user.name?.includes(searchWord) ||
        user.email?.includes(searchWord) ||
        hasKeywords ||
        hasCustomRoles
      )
    })
  }, [internalUsers, searchWord, watch])

  const filteredExternalUsers = useMemo(() => {
    const externalAssignedUsers = watch('externalEditors')

    if (!searchWord) {
      return externalAssignedUsers
    }

    return externalAssignedUsers.filter(user => {
      const userKeywords = externalUsers.find(externalUser => externalUser.id === user.id)?.keywords
      const hasKeywords = userKeywords?.some(keyword => keyword.includes(searchWord)) ?? false

      return user.name?.includes(searchWord) || user.email?.includes(searchWord) || hasKeywords
    })
  }, [externalUsers, searchWord, watch])

  return (
    <div className={className}>
      <div className="tw-sticky tw-top-0 tw-z-30 tw-bg-surface-primary">
        <div className="tw-flex tw-gap-[1.71rem] tw-items-center">
          <ProjectAssignSegmentedControls
            internalUserCompassRoleIds={filteredInternalUsers.map(user => user.compassRoleId)}
            externalUserCompassRoleIds={filteredExternalUsers.map(user => user.compassRoleId)}
            compassRoles={compassRoles}
            selectedId={selectedTab}
            onSelect={setSelectedTab}
            className="tw-w-full"
          />
          <Search
            onChange={setSearchWord}
            size="s"
            placeholder="検索"
            className="tw-w-[20rem] tw-ml-auto !tw-rounded-[0.43rem] !tw-h-[2.57rem]"
          />
        </div>

        <div className="tw-mt-s3">
          {selectedTab === 'internal' ? (
            <InternalAssignUserEditTableTop
              profile={profile}
              watch={watch}
              setValue={setValue}
              sortItem={internalSortItem}
              sortType={internalSortType}
              onSelectItem={setInternalSortItem}
              onSelectSortType={setInternalSortType}
              className="tw-border-t tw-border-x tw-border-border-secondary tw-rounded-t-[8px]"
            />
          ) : (
            <ExternalAssignUserEditTableTop
              watch={watch}
              setValue={setValue}
              sortItem={externalSortItem}
              sortType={externalSortType}
              onSelectItem={setExternalSortItem}
              onSelectSortType={setExternalSortType}
              className="tw-border-t tw-border-x tw-border-border-secondary tw-rounded-t-[8px]"
            />
          )}
        </div>
      </div>

      <div>
        {selectedTab === 'internal' ? (
          <>
            <InternalAssignUserEditTableBody
              alreadyAssignedInternalUsers={project.internalUsers}
              displayedAssignedInternalUsers={filteredInternalUsers}
              watch={watch}
              setValue={setValue}
              formErrors={formErrors}
              trigger={trigger}
              compassRoles={compassRoles}
              profile={profile}
              sortType={internalSortType}
              sortItem={internalSortItem}
              className="tw-border-b tw-border-x tw-border-border-secondary tw-rounded-b-[8px]"
            />
            {filteredInternalUsers.length === 0 && (
              <NoAssignedUserNotice isHiddenAddUserButton className="tw-py-[10rem]" />
            )}
          </>
        ) : (
          <>
            <ExternalAssignUserEditTableBody
              alreadyAssignedExternalUsers={project.externalUsers}
              displayedAssignedExternalUsers={filteredExternalUsers}
              watch={watch}
              setValue={setValue}
              formErrors={formErrors}
              trigger={trigger}
              compassRoles={compassRoles}
              sortItem={externalSortItem}
              sortType={externalSortType}
              className="tw-border-b tw-border-x tw-border-border-secondary tw-rounded-b-[8px]"
            />
            {filteredExternalUsers.length === 0 && (
              <NoAssignedUserNotice isHiddenAddUserButton className="tw-py-[10rem]" />
            )}
          </>
        )}
      </div>
    </div>
  )
}
